/**
 * The information extraction — the `extraction` document of a `support` agent_version (plan-018 DOM, design §30.3.7,
 * §30.0.3 D1; reference schema `wetalk-information-index-v1`).
 *
 * The index reference and the information card live INSIDE `config_document.extraction`, never as top-level keys of
 * the runtime configuration document: `RuntimeConfigSchema` is unchanged, so every stored document — of every
 * template, before and after this release — parses exactly as before. `AgentRuntimeConfig.extraction` stays `unknown`,
 * and {@link informationExtractionOf} narrows it by re-running one zod schema, beside `deliveryExtractionOf` and
 * `surveyExtractionOf`. The runtime calls it once per agent_version (its config cache is keyed by the immutable id).
 *
 * The passages themselves are in the information index, never in the document.
 */
import { z } from "zod";
import { agentDocumentId } from "../id/codec.js";
import { idCodec } from "../id/brand.js";
import { idSchema } from "../id/schema.js";
import { ValidationError } from "../error/taxonomy.js";
import { isLanguageCode } from "../template/language-code.js";
import { INFORMATION_CARD_CONTACT_LIMIT, INFORMATION_CARD_DESCRIPTION_CHARACTER_LIMIT, INFORMATION_CARD_STATED_HOURS_CHARACTER_LIMIT, INFORMATION_DOCUMENT_FILE_LIMIT, INFORMATION_PASSAGE_LIMIT, } from "./limit.js";
import { TEXT_SEARCH_CONFIG } from "./text-search-config.js";
import { websiteReadingIdCodec } from "./website-reading.js";
/** The reference schema of a `support` version's extraction (the catalogue row's `reference_schema`). */
export const INFORMATION_REFERENCE_SCHEMA = "wetalk-information-index-v1";
/** The id codec of an information index (UUID v7, minted in the application). */
export const informationIndexIdCodec = idCodec("InformationIndexId");
/** Where a card value was copied from. */
export const CARD_VALUE_SOURCE_KIND = ["pasted", "document", "website", "structured_data"];
const LanguageCodeSchema = z
    .string()
    .refine((value) => isLanguageCode(value), { message: "not a BCP-47 language code" })
    .transform((value) => value);
const CardValueSchema = z
    .object({
    text: z.string().min(1),
    source: z.object({ kind: z.enum(CARD_VALUE_SOURCE_KIND), ref: z.string().min(1) }).strict(),
})
    .strict();
const boundedCardValue = (limit) => CardValueSchema.refine((value) => value.text.length <= limit, {
    message: `at most ${String(limit)} characters`,
});
const InformationCardSchema = z
    .object({
    sourceLanguage: LanguageCodeSchema,
    organizationName: CardValueSchema.nullable(),
    description: boundedCardValue(INFORMATION_CARD_DESCRIPTION_CHARACTER_LIMIT).nullable(),
    address: CardValueSchema.nullable(),
    phone: z.array(CardValueSchema).max(INFORMATION_CARD_CONTACT_LIMIT),
    email: z.array(CardValueSchema).max(INFORMATION_CARD_CONTACT_LIMIT),
    websiteAddress: CardValueSchema.nullable(),
    statedHours: boundedCardValue(INFORMATION_CARD_STATED_HOURS_CHARACTER_LIMIT).nullable(),
    translated: z.record(z.string().refine((value) => isLanguageCode(value), { message: "not a BCP-47 language code" }), z.object({ description: z.string().min(1).nullable(), statedHours: z.string().min(1).nullable() }).strict()),
})
    .strict();
/** The one validator of an information extraction. `.strict()`: an unknown key is producer/consumer skew. */
export const InformationExtractionSchema = z
    .object({
    schema: z.literal(INFORMATION_REFERENCE_SCHEMA),
    informationIndexId: idSchema(informationIndexIdCodec),
    websiteReadingId: idSchema(websiteReadingIdCodec).nullable(),
    embeddingModelSha256: z.string().regex(/^[0-9a-f]{64}$/, "not a lowercase hex SHA-256"),
    textSearchConfigSet: z
        .array(z.enum(TEXT_SEARCH_CONFIG))
        .min(1)
        .refine((value) => new Set(value).size === value.length, { message: "configurations repeat" }),
    passageCount: z.number().int().min(1).max(INFORMATION_PASSAGE_LIMIT),
    inputSummary: z
        .object({
        pastedTextDocumentId: idSchema(agentDocumentId).nullable(),
        documentId: z.array(idSchema(agentDocumentId)).max(INFORMATION_DOCUMENT_FILE_LIMIT),
        websiteAddress: z.string().min(1).nullable(),
    })
        .strict(),
    card: InformationCardSchema.nullable(),
})
    .strict();
/** The information extraction in `extraction`, or `null` when it is not one (another template's document, or skew). */
export function informationExtractionOfDocument(extraction) {
    const parsed = InformationExtractionSchema.safeParse(extraction);
    return parsed.success ? parsed.data : null;
}
/**
 * The information extraction of a `support` version. Throws `ValidationError` (field `extraction`) when the version's
 * extraction is not one: a configuration that cannot serve fails the conversation, it is never served a guess.
 */
export function informationExtractionOf(config) {
    const extraction = informationExtractionOfDocument(config.extraction);
    if (extraction === null) {
        throw new ValidationError({
            code: "validation_failed",
            message: `agent_version ${String(config.agentVersionId)} carries no ${INFORMATION_REFERENCE_SCHEMA} extraction.`,
            field: "extraction",
        });
    }
    return extraction;
}
//# sourceMappingURL=information-extraction.js.map